import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { I18n } from 'react-redux-i18n';
import Modal from '../reusable/NewModal';
import apiInstance2 from '../../services/apiService';
import { errorMessages } from '../../constants/errorMessages';
import createToastNotification from '../../utils/createToastNotification';
import { selectAvailableLanguages } from '../../store/user/selectors';
import AddCompetencyCategoryForm from './AddCompetencyCategoryForm';

const AddCompetencyCategory = ({ category, onCancel, onClose }) => {
  const languages = useSelector(selectAvailableLanguages);
  const [formData, setFormData] = useState({
    languages: [
      {
        lang: { value: '', code: '', label: '' },
        name: '',
        description: '',
      },
    ],
  });

  const isUpdating = category && category.competencyID;

  useEffect(() => {
    if (isUpdating && languages && languages.length) {
      const haveLanguages = category.languages && category.languages[0]?.langID;

      let languagesObject;

      if (!haveLanguages) {
        languagesObject = [
          {
            lang: languages.find((language) => language.value === category.langID || 'EN'),
            name: category.name,
            description: category.description,
          },
        ];
      } else {
        languagesObject = category.languages.map((data) => ({
          lang: languages.find((language) => language.value === data.langID),
          name: data.name,
          description: data.description,
        }));
      }

      setFormData({
        languages: languagesObject,
      });
    }
  }, [isUpdating, category, languages]);

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      setSubmitting(true);

      const languagesObject = values.languages.map((data) => ({
        langID: data.lang.value,
        name: data.name,
        description: data.description,
      }));

      const data = {
        name: languagesObject[0].name,
        description: languagesObject[0].description,
        customLanguages: languagesObject,
      };

      const afterData = data;

      const method = isUpdating
        ? apiInstance2.patch('/api/v2/360/competencies', { ...data, competencyID: category.competencyID })
        : apiInstance2.post(`/api/v2/360/competency-categories`, data);

      method
        .then(({ data }) => {
          setSubmitting(false);
          createToastNotification({
            message: I18n.t(isUpdating ? 'categoryUpdateToast' : 'newCategoryToast'),
          });
          onClose({ languages: afterData.customLanguages, ...data }, !isUpdating);
        })
        .catch((e) => {
          console.log(e);
          setSubmitting(false);
        });
    },
    [category, isUpdating],
  );

  const validationSchema = Yup.object().shape({
    languages: Yup.array().of(
      Yup.object().shape({
        lang: Yup.object()
          .shape({
            value: Yup.string().required(),
            code: Yup.string().required(),
            label: Yup.string().required(),
          })
          .required(errorMessages.selectField),
        name: Yup.string()
          .required(errorMessages.inputField)
          .max(100, errorMessages.tooLong(100)),
        description: Yup.string()
          .required(errorMessages.inputField)
          .max(500, errorMessages.tooLong(500)),
      }),
    ),
  });

  return (
    <Modal
      isVisible
      onClose={onCancel}
      title={isUpdating ? I18n.t('Edit competency category') : I18n.t('Add competency category')}
    >
      <Formik
        enableReinitialize
        initialValues={formData}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        component={AddCompetencyCategoryForm}
      />
    </Modal>
  );
};

export default AddCompetencyCategory;
